import { useAtom } from '@reatom/npm-react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { Button, CheckMarkIcon } from '@blade/ui-kit'
import { PlatformUtil } from '@root/shared'
import { AnnouncementHeader } from '@root/shared/ui/announcement/announcement-header'
import {
  Checkbox,
  DebugJson,
  ExternalUrlIcon,
  Separator as Separator_,
} from '@root/shared/ui-kit'

import { skipUnsafeUrlWarningAtom } from '../atoms'
import * as UnsafeUrlProvider from '../model'

export type UnsafeUrlDialogAnnouncementProps = {
  unsafeUrl: string | URL
  unsafeUrlProvider: UnsafeUrlProvider.UnsafeUrlProvider
  onClose: () => void
  withHeader?: boolean
}

export const UnsafeUrlDialogAnnouncement = ({
  unsafeUrl,
  unsafeUrlProvider,
  onClose,
  withHeader = true,
}: UnsafeUrlDialogAnnouncementProps) => {
  const { t } = useTranslation()
  const [skipUnsafeUrlWarning, setSkipUnsafeUrlWarning] = useAtom(
    skipUnsafeUrlWarningAtom,
  )

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    e.preventDefault()

    PlatformUtil.openLink(unsafeUrl, '_blank')
    onClose()
  }
  const toggleSkipWarning = (checked: boolean | 'indeterminate') =>
    setSkipUnsafeUrlWarning(
      typeof checked === 'boolean' ? checked : skipUnsafeUrlWarning,
    )

  return (
    <Root>
      {withHeader ? (
        <AnnouncementHeader
          title={t('unsafe-url-dialog.title')}
          bigTitle={t('unsafe-url-dialog.description')}
          onClose={onClose}
        />
      ) : undefined}
      <Body>
        <Heading>{t('unsafe-url-dialog.beware-of-phishing-scams')}</Heading>
        <Description>
          {t('unsafe-url-dialog.never-enter-your-credentials')}
        </Description>
        <Separator />
        <Heading>{t('unsafe-url-dialog.protect-your-wallet')}</Heading>
        <Description>
          {t('unsafe-url-dialog.never-connect-your-wallet')}
        </Description>
        <Separator />
        <Heading>{t('unsafe-url-dialog.when-in-doubt-stop')}</Heading>
        <Description>
          {t('unsafe-url-dialog.if-something-feels-off')}
        </Description>
        <Separator />
        <DebugJson value={unsafeUrl.toString()} />
      </Body>
      <Footer>
        <ButtonAlt
          variant={'solid'}
          color={'primary'}
          fullWidth
          radius={'rounded'}
          onClick={handleClick}
          iconRight={<ExternalUrlIcon size={16} color="var(--textA-11)" />}
        >
          <CheckMarkIcon size={14} />
          <UnsafeUrlConfirmation>
            {t('unsafe-url-dialog.i-understand')}
          </UnsafeUrlConfirmation>
          {t('unsafe-url-dialog.continue-on')}&nbsp;
          <UnsafeUrlProviderName>
            {UnsafeUrlProvider.displayString(unsafeUrlProvider)}
          </UnsafeUrlProviderName>
        </ButtonAlt>
        <Note>
          <Checkbox
            id="skip-warning"
            checked={skipUnsafeUrlWarning}
            onCheckedChange={(checked) => toggleSkipWarning(checked)}
          />
          <NoteLabel htmlFor="skip-warning">
            {t('unsafe-url-dialog.dont-show-this-warning-again')}
          </NoteLabel>
        </Note>
      </Footer>
    </Root>
  )
}

const Root = styled.div`
  overflow: hidden;
`

const Body = styled.div`
  padding: 20px 22px 4px;

  font-size: 14px;
  color: var(--text-10);
`

const Footer = styled.div`
  padding: 0 20px 20px;
  gap: 20px;

  display: grid;
  gap: 10px;
  place-items: center;
`

const Note = styled.div`
  color: var(--text-10);

  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px;

  cursor: pointer;

  display: grid;
  grid-template-columns: auto auto;
  gap: 6px;
  place-items: center;
`

const NoteLabel = styled.label`
  color: var(--text-10);

  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px;

  cursor: pointer;
`

const Heading = styled.h2`
  color: var(--text-12);

  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px;
`

const Description = styled.p`
  color: var(--text-9);

  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
`

const Separator = styled(Separator_)`
  margin: 16px 0;
  opacity: 0.4;
`

const ButtonAlt = styled(Button)`
  color: var(--textA-11);
  gap: 0;

  > svg {
    margin-inline-end: 6px;
    margin-inline-start: 4px;
  }
`

const UnsafeUrlConfirmation = styled.span`
  border-inline-end: 1px solid rgba(194, 206, 255, 0.32);
  color: var(--text-12);

  font-weight: 700;
  height: 18px;
  margin-inline-end: 12px;
  padding-inline-end: 12px;

  display: grid;
  place-content: center;
`

const UnsafeUrlProviderName = styled.span`
  text-transform: capitalize;
`
